import { useEffect, useState, useCallback } from 'react';
import { loadPermissions, savePermissions, PERMISSION_DESCRIPTIONS, CATEGORIES, ROLE_VALUES } from '../../utils/permissions';
import { fetchAPI } from '../../utils/utils';
import RolePermissionsLogs from './RolePermissionsLogs';

function RolePermissionsEditor({ userInfo }) {
    const [perms, setPerms] = useState({});
    const [saving, setSaving] = useState(false);
    const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [saveTimeout, setSaveTimeout] = useState(null);
    const [pendingChanges, setPendingChanges] = useState([]);

    useEffect(() => {
        loadPermissions().then(p => setPerms(p || {}));
        
        // Carregar logs
        setLoadingLogs(true);
        fetchAPI('/role_permissions_logs', (data) => {
            setLogs(data || []);
            setLoadingLogs(false);
        });
    }, []);

    const addLocalLog = useCallback((permId, role, oldValue, newValue) => {
        const newLog = {
            user_name: userInfo?.nom || 'Tu',
            timestamp: new Date().toISOString(),
            changes: [{
                permission: permId,
                role: role,
                old_value: oldValue,
                new_value: newValue
            }]
        };

        // Afegir el nou log al principi de la llista
        setLogs(prevLogs => [newLog, ...prevLogs]);
        
        // Afegir a la llista de canvis pendents per si cal consolidar-los
        setPendingChanges(prev => [...prev, { permId, role, oldValue, newValue }]);
    }, [userInfo]);

    const autoSave = useCallback((newPerms) => {
        // Cancel·lar timeout anterior si existeix
        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        setSaving(true);
        
        // Esperar 500ms abans de guardar (debouncing)
        const timeout = setTimeout(async () => {
            await savePermissions(newPerms, userInfo?.id);
            setSaving(false);
            setPendingChanges([]); // Netejar canvis pendents després de guardar
            
            // Recarregar logs després de guardar per sincronitzar amb el servidor
            fetchAPI('/role_permissions_logs', (data) => {
                setLogs(data || []);
            });
        }, 500);
        
        setSaveTimeout(timeout);
    }, [saveTimeout, userInfo]);

    const update = (id, role, value) => {
        const oldValue = perms[id]?.[role]?.min || 0;
        const newValue = parseInt(value);
        
        // No fer res si el valor no ha canviat
        if (oldValue === newValue) return;
        
        const newPerms = {
            ...perms,
            [id]: {
                ...perms[id],
                [role]: { ...(perms[id]?.[role] || {}), min: newValue },
                op: 'OR' // Always use OR by default
            }
        };
        
        setPerms(newPerms);
        addLocalLog(id, role, oldValue, newValue);
        autoSave(newPerms);
    };





    const renderRoleSelector = (permId, role, value) => {
        return (
            <div className="role-selector">
                {Object.entries(ROLE_VALUES).map(([val, config]) => (
                    <button
                        key={val}
                        className={`role-btn ${config.class} ${value == val ? 'active' : ''}`}
                        onClick={() => update(permId, role, val)}
                    >
                        {config.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderPermissionRow = (id) => {
        const perm = perms[id] || {};
        const info = PERMISSION_DESCRIPTIONS[id];
        
        return (
            <div key={id} className="permission-row">
                <div className="permission-info">
                    <h4>{info?.name || id}</h4>
                    <p>{info?.description || 'Sense descripció'}</p>
                </div>
                
                <div className="permission-controls">
                    <div className="role-control">
                        <label>Tècnica:</label>
                        {renderRoleSelector(id, 'tecnica', perm.tecnica?.min || 0)}
                    </div>
                    
                    <div className="role-control">
                        <label>Junta:</label>
                        {renderRoleSelector(id, 'junta', perm.junta?.min || 0)}
                    </div>
                </div>
            </div>
        );
    };

    const groupedPermissions = Object.keys(perms).reduce((acc, permId) => {
        const category = PERMISSION_DESCRIPTIONS[permId]?.category || 'altres';
        if (!acc[category]) acc[category] = [];
        acc[category].push(permId);
        return acc;
    }, {});

    return (
        <div className="role-permissions-editor">
            <style jsx>{`
                .role-permissions-editor {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 20px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }

                .header {
                    margin-bottom: 30px;
                }

                .header h1 {
                    color: #333;
                    margin-bottom: 10px;
                }

                .header p {
                    color: #666;
                    font-size: 16px;
                }

                .category-section {
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 20px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }

                .category-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 20px;
                    padding-bottom: 15px;
                    border-bottom: 2px solid #e0e0e0;
                }

                .category-icon {
                    font-size: 24px;
                    margin-right: 10px;
                }

                .category-title {
                    font-size: 20px;
                    font-weight: 600;
                    color: #444;
                    margin: 0;
                }

                .permission-row {
                    background: white;
                    border-radius: 8px;
                    padding: 20px;
                    margin-bottom: 15px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 20px;
                }

                .permission-info {
                    flex: 1;
                    min-width: 300px;
                }

                .permission-info h4 {
                    margin: 0 0 5px 0;
                    color: #333;
                    font-size: 16px;
                }

                .permission-info p {
                    margin: 0;
                    color: #666;
                    font-size: 14px;
                }

                .permission-controls {
                    display: flex;
                    gap: 20px;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .role-control {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .role-control label {
                    font-size: 12px;
                    color: #666;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .role-selector {
                    display: flex;
                    gap: 4px;
                    background: #f0f0f0;
                    padding: 3px;
                    border-radius: 6px;
                }

                .role-btn {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.2s;
                    background: transparent;
                    color: #666;
                }

                .role-btn:hover {
                    background: rgba(0,0,0,0.05);
                }

                .role-btn.active {
                    color: white;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .role-btn.no.active {
                    background: #dc3545;
                }

                .role-btn.membre.active {
                    background: #ffc107;
                    color: #333;
                }

                .role-btn.cap.active {
                    background: #28a745;
                }





                .info-box {
                    background: #e3f2fd;
                    border: 1px solid #90caf9;
                    border-radius: 8px;
                    padding: 15px 20px;
                    margin-bottom: 20px;
                    color: #1565c0;
                }

                .info-box h3 {
                    margin: 0 0 10px 0;
                    font-size: 16px;
                }

                .info-box ul {
                    margin: 0;
                    padding-left: 20px;
                }

                .info-box li {
                    margin-bottom: 5px;
                }

                @media (max-width: 768px) {
                    .permission-row {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .permission-controls {
                        flex-direction: column; /* stack tècnica and junta vertically */
                        gap: 12px;
                    }

                    .role-control {
                        flex: 1;
                    }

                    /* Make the role selector fill the width and turn buttons into equal segments */
                    .role-selector {
                        width: 100%;
                        display: flex;
                    }

                    .role-btn {
                        flex: 1 1 33%;
                        text-align: center;
                        padding: 12px 6px;
                        font-size: 14px;
                        border-radius: 0; /* square edges when in group */
                    }

                    /* Add subtle separators between the segmented buttons */
                    .role-btn:not(:last-child) {
                        border-right: 1px solid rgba(0,0,0,0.05);
                    }

                    /* Restore rounded corners for the group */
                    .role-selector .role-btn:first-child {
                        border-top-left-radius: 6px;
                        border-bottom-left-radius: 6px;
                    }
                    .role-selector .role-btn:last-child {
                        border-top-right-radius: 6px;
                        border-bottom-right-radius: 6px;
                    }
                }

                .saving-indicator {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: white;
                    border: 1px solid #28a745;
                    border-radius: 8px;
                    padding: 10px 20px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    z-index: 1000;
                }

                .spinner {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #f3f3f3;
                    border-top: 2px solid #28a745;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>

            <div className="header">
                <h1>Gestió dels rols</h1>
                <p>Configura els permisos per a cada rol de la colla</p>
            </div>

            {Object.entries(CATEGORIES).map(([catId, catInfo]) => {
                const categoryPerms = groupedPermissions[catId] || [];
                if (categoryPerms.length === 0) return null;

                return (
                    <div key={catId} className="category-section">
                        <div className="category-header">
                            <span className="category-icon">{catInfo.icon}</span>
                            <h2 className="category-title">{catInfo.name}</h2>
                        </div>
                        {categoryPerms.map(permId => renderPermissionRow(permId))}
                    </div>
                );
            })}

            {saving && (
                <div className="saving-indicator">
                    <div className="spinner"></div>
                    <span>Desant...</span>
                </div>
            )}

            <RolePermissionsLogs logs={logs} loading={loadingLogs} />
        </div>
    );
}

export default RolePermissionsEditor;
